import React, { useEffect, useState } from 'react';
import { BLOCK_LABELS } from '../Landing/HomePageView';

// Section bar of the Home Page editor, like the Employer Template Editor's: reorder, hide and
// restore the blocks below the main banner. Dragging works with a mouse; every drag action
// also has a button, and each change keeps focus on a sensible control and is announced.

function jumpTo(key) {
  const block = document.getElementById(`hpe-block-${key}`);
  if (!block) return;
  block.scrollIntoView({ block: 'start' });
  block.querySelector('input, textarea, button, a[href]')?.focus();
}

export default function BlocksManager({ ctx }) {
  const blocks = ctx.content.blocks || [];
  const visible = blocks.filter((b) => b.visible);
  const hidden = blocks.filter((b) => !b.visible);
  const [dragKey, setDragKey] = useState(null);
  const [pendingFocus, setPendingFocus] = useState(null);

  useEffect(() => {
    if (!pendingFocus) return;
    document.getElementById(pendingFocus)?.focus();
    setPendingFocus(null);
  }, [pendingFocus]);

  const label = (key) => BLOCK_LABELS[key] || key;

  // Reorder among the visible blocks; hidden ones keep their place at the end of the list
  const reorderVisible = (nextVisible) => {
    ctx.onChange('blocks', [...nextVisible, ...hidden]);
  };

  const move = (index, delta) => {
    const target = index + delta;
    const next = [...visible];
    [next[index], next[target]] = [next[target], next[index]];
    reorderVisible(next);
    const key = next[target].key;
    const button = delta < 0 ? (target === 0 ? 'later' : 'earlier') : (target === next.length - 1 ? 'earlier' : 'later');
    setPendingFocus(`hpe-chip-${key}-${button}`);
    ctx.announce(`${label(key)} moved to position ${target + 1} of ${next.length}.`);
  };

  const drop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return setDragKey(null);
    const next = [...visible];
    const from = next.findIndex((b) => b.key === dragKey);
    const to = next.findIndex((b) => b.key === targetKey);
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    reorderVisible(next);
    ctx.announce(`${label(dragKey)} moved to position ${to + 1} of ${next.length}.`);
    return setDragKey(null);
  };

  const setVisible = (key, isVisible) => {
    const rest = blocks.filter((b) => b.key !== key);
    // A restored block goes back at the end of the visible ones
    const next = isVisible
      ? [...rest.filter((b) => b.visible), { key, visible: true }, ...rest.filter((b) => !b.visible)]
      : [...rest, { key, visible: false }];
    ctx.onChange('blocks', next);
    setPendingFocus(isVisible ? `hpe-chip-${key}` : `hpe-restore-${key}`);
    ctx.announce(isVisible ? `${label(key)} shown at the end of the page.` : `${label(key)} hidden from visitors.`);
  };

  const resetOrder = () => {
    const order = Object.keys(BLOCK_LABELS);
    ctx.onChange('blocks', order.map((key) => ({ key, visible: true })));
    ctx.announce('All sections shown in the default order.');
  };

  return (
    <section className="hpe-blocks" aria-labelledby="hpe-blocks-title">
      <div className="hpe-blocks-head">
        <h2 id="hpe-blocks-title" className="hpe-blocks-title">Sections</h2>
        <button type="button" className="hpe-blocks-reset" onClick={resetOrder}>Reset positions</button>
      </div>
      <p className="hpe-blocks-hint">
        The header and main banner always come first. Drag a section, or use its arrow buttons, to change the order.
        Select a section's name to jump to it.
      </p>
      <ol className="hpe-chips">
        {visible.map((b, i) => (
          <li
            key={b.key}
            className={`hpe-chip${dragKey === b.key ? ' is-dragging' : ''}`}
            draggable
            onDragStart={() => setDragKey(b.key)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(b.key)}
            onDragEnd={() => setDragKey(null)}
          >
            <span className="hpe-chip-grip" aria-hidden="true">⋮⋮</span>
            <button type="button" id={`hpe-chip-${b.key}`} className="hpe-chip-name" onClick={() => jumpTo(b.key)}>
              <span className="sr-only">Go to </span>{label(b.key)}
            </button>
            <button type="button" id={`hpe-chip-${b.key}-earlier`} className="hpe-mini" disabled={i === 0}
              onClick={() => move(i, -1)} aria-label={`Move ${label(b.key)} earlier`}>←</button>
            <button type="button" id={`hpe-chip-${b.key}-later`} className="hpe-mini" disabled={i === visible.length - 1}
              onClick={() => move(i, 1)} aria-label={`Move ${label(b.key)} later`}>→</button>
            <button type="button" className="hpe-mini hpe-mini--danger" onClick={() => setVisible(b.key, false)}
              aria-label={`Hide ${label(b.key)}`}>×</button>
          </li>
        ))}
      </ol>
      {hidden.length > 0 && (
        <div className="hpe-hidden">
          <h3 className="hpe-hidden-title">Hidden sections</h3>
          <ul className="hpe-hidden-list">
            {hidden.map((b) => (
              <li key={b.key}>
                <button type="button" id={`hpe-restore-${b.key}`} className="hpe-restore" onClick={() => setVisible(b.key, true)}>
                  Restore {label(b.key)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
